import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import { springs } from "@funnel-ui/motion";
import { ProgressIndicator, type ProgressIndicatorStyle } from "@funnel-ui/shell/ProgressIndicator";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

type FunnelHeaderProps = {
  brandName?: string;
  /** Brand logo image URL. When provided, renders an <img> instead of the letter box + name. */
  logoUrl?: string;
  logoMark?: ReactNode;
  showBack?: boolean;
  onBack?: () => void;
  progress?: {
    style: ProgressIndicatorStyle;
    current: number;
    total: number;
  };
  renderProgress?: () => ReactNode;
  showLogin?: boolean;
  onLogin?: () => void;
  loginLabel?: string;
  compactLogin?: boolean;
  skipTargetId?: string;
  skipLabel?: string;
  className?: string;
  testId?: string;
};

const BackButton = ({ onBack }: { onBack?: () => void }) => {
  const prefersReduced = useReducedMotion();
  return (
    <m.span
      initial={{ x: prefersReduced ? 0 : -6, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: prefersReduced ? 0 : -6, opacity: 0 }}
      transition={prefersReduced ? { duration: 0 } : springs.snappy}
      className="inline-flex"
    >
      <Button
        isIconOnly
        variant="ghost"
        size="sm"
        aria-label="Go back"
        onPress={onBack}
        className="shrink-0 rounded-full"
      >
        <Icon icon="gravity-ui:arrow-left" width={18} height={18} />
      </Button>
    </m.span>
  );
};

export const FunnelHeader = ({
  brandName = "Brand",
  logoUrl,
  logoMark,
  showBack,
  onBack,
  progress,
  renderProgress,
  showLogin = true,
  onLogin,
  loginLabel = "Log in",
  compactLogin = false,
  skipTargetId = "step-main",
  skipLabel = "Skip to content",
  className,
  testId,
}: FunnelHeaderProps) => {
  const progressNode = renderProgress
    ? renderProgress()
    : progress && <ProgressIndicator {...progress} />;

  return (
    <div
      data-testid={testId}
      className={`flex items-center gap-3 px-5 pt-3.5 pb-3 ${className ?? ""}`}
    >
      <a
        href={`#${skipTargetId}`}
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-50 focus:rounded-md focus:bg-[hsl(var(--brand-primary))] focus:px-3 focus:py-1.5 focus:text-sm focus:font-medium focus:text-[hsl(var(--brand-primary-foreground))] focus:outline-none"
      >
        {skipLabel}
      </a>

      <AnimatePresence initial={false}>
        {showBack && <BackButton onBack={onBack} />}
      </AnimatePresence>

      <div className="flex shrink-0 items-center gap-2">
        {logoUrl ? (
          <ResponsiveAsset
            src={logoUrl}
            alt={brandName}
            loading="eager"
            widthHint={140}
            className="h-7 max-w-[140px] object-contain"
          />
        ) : (
          <>
            {logoMark ?? (
              <span
                aria-hidden="true"
                className="grid h-7 w-7 place-items-center rounded-lg text-sm font-bold leading-none"
                style={{
                  background: "hsl(var(--brand-primary))",
                  color: "hsl(var(--brand-primary-foreground))",
                  fontFamily: "var(--brand-font-display)",
                }}
              >
                {brandName.slice(0, 1).toUpperCase()}
              </span>
            )}
            <span
              className="text-sm font-semibold"
              style={{
                fontFamily: "var(--brand-font-display)",
                letterSpacing: "-0.01em",
                color: "hsl(var(--brand-bg-foreground))",
              }}
            >
              {brandName}
            </span>
          </>
        )}
      </div>

      <div className="min-w-0 flex-1 pl-1">{progressNode}</div>

      {showLogin &&
        (compactLogin ? (
          <Button
            isIconOnly
            variant="ghost"
            size="sm"
            aria-label={loginLabel}
            onPress={onLogin}
            className="shrink-0 rounded-full"
          >
            <Icon icon="gravity-ui:person" width={18} height={18} />
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onPress={onLogin} className="shrink-0">
            {loginLabel}
          </Button>
        ))}
    </div>
  );
};

export default FunnelHeader;
